import { gql, useQuery } from "@apollo/client";
import { newTransactionSubscription } from "../FindTransactions";
import { 
  Transaction, 
  QueryFindTransactionsByRecipientWalletIdArgs 
} from "../../../../schemaTypes";
import { useCallback } from "react";

export const findTransactionsByRecipientWalletIdQuery = gql`
  query FindTransactionsByRecipientWalletIdQuery(
    $page: Int!
    $pageSize: Int!
    $duration: String
    $statusFilter: String
    $typeFilter: String
    $searchText: String
  ) {
    findTransactionsByRecipientWalletId(
      page: $page
      pageSize: $pageSize
      duration: $duration
      statusFilter: $statusFilter
      typeFilter: $typeFilter
      searchText: $searchText
    ) {
      pageTransactions{
        data {
          id
          amount
          charge
          reference
          type
          status
          currency
          transferTypes
          # wallet {
          #   id
          #   balance
          #   owner {
          #     id
          #     customer{
          #       firstname
          #       lastname
          #     }
          #   }
          #   createdAt
          #   updatedAt
          # }
          # recipientWallet {
          #   id
          #   balance
          #   currency
          #   owner {
          #     admin {
          #       merchantProfile {
          #         companyName
          #       }
          #     }
          #   }
          #   createdAt
          #   updatedAt
          # }
          bankPreference{
            id
          }
          offer {
            id
            title
            description
            bannerUrl
            category {
              id
              name
              imgSrc
              createdBy{
                id
                role
              }
              createdAt
              updatedAt
            }
            offerType
            discountType
            discountValue
            partnerVoucher
            amount
            referralUrl
            status
            usageLimitPerUser
            totalUsageLimit
            startDate
            endDate
            community{
              id
              name
              alias
            }
            redemptions
            revenueGenerated
            clickThroughs
            featured
            createdBy{
              id
              role
              admin {
                id
                shortId
                firstname
                lastname
                email
                phone
                merchantProfile {
                  id
                  businessName
                  businessType
                  regCountry
                  businessRegNumber
                  taxId
                  address
                  addressAlt
                  city
                  country
                  postcode
                  companyName
                  username
                  industry {
                    id
                    name
                    createdBy{
                      id
                      role
                    }
                    createdAt
                    updatedAt
                  }
                  companyLocation
                  logoUrl
                  bannerUrl
                  description
                  commissionRate
                  createdAt
                  updatedAt
                }
                createdAt
                updatedAt
              }
            }
            # njoyCode {
            #   id
            #   code
            #   redeemedAt
            #   createdAt
            #   updatedAt
            # }
            createdAt
            updatedAt
          }
          njoyCode
          partnerVoucher
          user {
            id
            # createdAt
            # updatedAt
          }
          createdAt
          updatedAt
        }
        total
        page
        pageSize
        totalPages
      }
      currentBalance
      amountWithdrawn
      amountReceived
    }
  }
`;

newTransactionSubscription;

export interface WithFindTransactionsByRecipientWalletId {
  transactions: {
    pageTransactions: {
      data: Transaction[]
      total: number;
      page: number;
      pageSize: number;
      totalPages: number;
    },
    currentBalance: number;
    amountWithdrawn: number;
    amountReceived: number;
  }
  loading: boolean;
  loadMore: () => void;
  hasMoreTransactions: boolean;
  page: number;
  pageSize: number;
  total: number;
  totalPages: number;
}

interface Props {
  variables: QueryFindTransactionsByRecipientWalletIdArgs;
  children: (data: WithFindTransactionsByRecipientWalletId) => React.ReactElement | null;
}

export const FindTransactionsByRecipientWalletId: React.FC<Props> = ({ children, variables }) => {
  const { data, loading, fetchMore } = useQuery<
    any,
    QueryFindTransactionsByRecipientWalletIdArgs
  >
  (findTransactionsByRecipientWalletIdQuery, {
    variables,
    notifyOnNetworkStatusChange: true,
  });

  const pageTransactions = data?.findTransactionsByRecipientWalletId?.pageTransactions || {
    data: [],
    total: 0,
    page: variables.page,
    pageSize: variables.pageSize,
    totalPages: 0,
  };

  const transactions = {
    pageTransactions,
    currentBalance: data?.findTransactionsByRecipientWalletId?.currentBalance ?? 0,
    amountWithdrawn: data?.findTransactionsByRecipientWalletId?.amountWithdrawn ?? 0,
    amountReceived: data?.findTransactionsByRecipientWalletId?.amountReceived ?? 0,
  };

  const page = pageTransactions.page;
  const pageSize = pageTransactions.pageSize;
  const total = pageTransactions.total;
  const totalPages = pageTransactions.totalPages;
  const hasMoreTransactions = page < totalPages;

  const loadMore = useCallback(() => {
    if (!hasMoreTransactions) return;
    fetchMore({
      variables: {
        ...variables,
        page: page + 1
      },
      updateQuery: (prev, { fetchMoreResult }) => {
        if (!fetchMoreResult || !fetchMoreResult.findTransactionsByRecipientWalletId) {
          return prev;
        }
        return {
          ...fetchMoreResult,
          findTransactionsByRecipientWalletId: {
            ...fetchMoreResult.findTransactionsByRecipientWalletId,
            data: [
              ...(prev?.findTransactionsByRecipientWalletId?.pageOffers?.data || []),
              ...(fetchMoreResult.findTransactionsByRecipientWalletId.pageOffers?.data || [])
            ]
          }
        };
      }
    });
  }, [fetchMore, variables, page, hasMoreTransactions]);

  return children({
    transactions: transactions as any,
    loading,
    loadMore,
    hasMoreTransactions,
    page,
    pageSize,
    total,
    totalPages,
  });
};